/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import {
  Dispatch,
  Fragment,
  SetStateAction,
  useCallback,
  useEffect,
  useState
} from "react";

import { useTranslation } from "react-i18next";

import PasswordValidation from "../../../../../../../../../Pages/Auth/components/PasswordValidation/PasswordValidation";
import usePasswordValidation from "../../../../../../../../../Pages/Auth/hooks/usePasswordValidation";
import { useStyles } from "../../../../../../../../../Pages/Auth/styles";
import {
  BUTTON_TYPE,
  PASSWORD_TYPE,
  PasswordInputsType,
  TooltipPswProps
} from "../../../../../../../../../Pages/Auth/types";
import { passwordLengthIsValid } from "../../../../../../../../../shared/functions/functions";
import InputField from "../../../../../../../../InputField/InputField";

interface IChangePasswordDialogContentProps {
  inputs: PasswordInputsType[];
  button: BUTTON_TYPE;
  setReady: Dispatch<SetStateAction<boolean>>;
  setPayload: Dispatch<SetStateAction<{}>>;
  resetValues: boolean;
  isLoading: boolean;
}

const ChangePasswordDialogContent = ({
  inputs,
  button,
  setReady,
  setPayload,
  resetValues,
  isLoading
}: IChangePasswordDialogContentProps) => {
  const { validatePasswordLength } = usePasswordValidation();
  const { t } = useTranslation();

  const [passwordError, setPasswordError] = useState<string>("");
  const [password, setPassword] = useState({
    current: "",
    repeat: "",
    newPsw: ""
  });

  const { blockWithBottomSpacing } = useStyles();

  const { current, repeat, newPsw } = password;

  useEffect(() => {
    resetValues &&
      setPassword({
        current: "",
        repeat: "",
        newPsw: ""
      });
  }, [resetValues]);

  useEffect(() => {
    setPayload({
      currentPassword: password.current,
      newPassword: password.newPsw,
      newPasswordConfirm: password.repeat
    });
  }, [password, setPayload]);

  const toggleDisable = useCallback(
    (value: boolean) => {
      setReady(value);
    },
    [setReady]
  );

  const handlePasswordLengthError = (
    key: string,
    params?: Record<string, unknown>
  ) => setPasswordError(t(key, params));

  const handleChange = useCallback(
    (e: { target: { name: string; value: string } }) => {
      const inputName = e.target.name;
      const inputValue = e.target.value;

      setPassword({
        ...password,
        [inputName]: inputValue
      });

      validatePasswordLength(e, handlePasswordLengthError);
    },
    [password, validatePasswordLength, handlePasswordLengthError]
  );

  const generateNewPasswordError = (inputName: PASSWORD_TYPE) => {
    const passwordValues = {
      [PASSWORD_TYPE.NewPsw]: newPsw,
      [PASSWORD_TYPE.Current]: current
    };

    if (inputName !== PASSWORD_TYPE.Repeat) {
      return passwordLengthIsValid(passwordValues[inputName])
        ? ""
        : passwordError;
    }

    return null;
  };

  const renderInputs = () =>
    inputs.map(
      (input: {
        TooltipPsw: TooltipPswProps;
        name: PASSWORD_TYPE;
        title: string;
      }) => {
        const { name, title } = input;

        const { PasswordVisibility, showPassword } = input.TooltipPsw;

        if (resetValues) {
          showPassword.current = false;
          showPassword.newPsw = false;
          showPassword.repeat = false;
        }

        return (
          <Fragment key={name}>
            <InputField
              testId={name}
              fullWidth
              size="medium"
              type={
                showPassword[name as keyof typeof showPassword]
                  ? "text"
                  : "password"
              }
              labelLeft={title}
              name={name}
              placeholder={t("Auth##enter here")}
              onChange={handleChange}
              disabled={isLoading}
              iconRight={<PasswordVisibility />}
              errorText={generateNewPasswordError(name)}
              onBlur={event =>
                validatePasswordLength(event, handlePasswordLengthError)
              }
              css={css(blockWithBottomSpacing)}
            />
          </Fragment>
        );
      }
    );

  return (
    <>
      {renderInputs()}

      <div>
        {/* validation rules: */}
        <PasswordValidation
          currentPsw={current}
          newPsw={newPsw}
          repeatPsw={repeat}
          onRulesMet={toggleDisable}
          name={button}
        />
      </div>
    </>
  );
};

export default ChangePasswordDialogContent;
